/**
 * Tag-Filterprädikat (#81) — geteilt zwischen Phase Board und Staffing.
 *
 * Liegt bewusst als reine Funktion neben der Seite (Muster: `phase-board-layout.ts`):
 * Die Regel ist die eigentliche Fachlichkeit dieses Filters und soll ohne gerenderte
 * Seite prüfbar sein. Beide Sichten importieren sie — es gibt keine zweite Fassung.
 */
import { NO_TAGS_VALUE } from '../../components/v2/filters/filter-primitives.js';

/**
 * Trägt das Projekt einen der gewählten Tags?
 *
 * - Leere Auswahl = **kein Filter**, nicht „keine Treffer".
 * - Mehrere Tags sind **ODER**-verknüpft: ein Treffer genügt. Eine UND-Verknüpfung ist
 *   bewusst nicht vorgesehen (keine zweite Betriebsart, kein verstecktes Umschalten).
 * - `NO_TAGS_VALUE` ist ein Eintrag wie jeder andere und wirkt genauso additiv: zusammen
 *   mit „SAP" gewählt, erscheinen Projekte ohne Tag **plus** Projekte mit „SAP".
 */
export function matchesTagFilter(
  tags: Array<{ tag_id: string }> | undefined,
  selected: string[],
): boolean {
  if (selected.length === 0) return true;
  const vorhanden = tags ?? [];
  if (selected.includes(NO_TAGS_VALUE) && vorhanden.length === 0) return true;
  return vorhanden.some((tg) => selected.includes(tg.tag_id));
}

/**
 * Welche Tag-Auswahl gilt beim Laden — aus dem gemerkten Filterstand (#81)?
 *
 * `undefined` heißt **nichts vorzubelegen**; die Seite lässt ihren Zustand dann in Ruhe.
 *
 * Die Reihenfolge ist der ganze Punkt und liegt deshalb hier statt in den beiden Seiten:
 * **`tags` gewinnt, wenn es existiert — auch als leeres Array.** Andernfalls bekäme
 * jemand, der seine Auswahl bewusst geleert hat, beim nächsten Aufruf den alten
 * Einzel-Tag zurück. Der Altwert `tag` (vor #81) wird nur übernommen, solange noch nie
 * eine Mehrfachauswahl gespeichert wurde — so überlebt eine Bestandsauswahl die
 * Umstellung, ohne eine bewusste Leerung zu überschreiben.
 *
 * Dass beide Sichten dieselbe Funktion rufen, ist die eigentliche Absicherung: Die
 * Reihenfolge lässt sich nicht mehr je Seite verdrehen.
 */
export function resolveTagFilterStart(stored: {
  // `| undefined` ausgeschrieben: Der Workspace steht auf `exactOptionalPropertyTypes`,
  // und die Aufrufer reichen die gemerkten Filter als Ganzes herein — dort sind die
  // Felder „vorhanden, aber undefined" ein zulässiger Zustand.
  tags?: string[] | undefined;
  tag?: string | null | undefined;
}): string[] | undefined {
  if (stored.tags !== undefined) return stored.tags;
  if (stored.tag) return [stored.tag];
  return undefined;
}
